import React, { useState, useEffect } from 'react';
import BrowserFrame from './BrowserFrame';

const content = {
  fr: {
    tag: 'Actualités',
    title: 'Notre vision pour l\'Afrique numérique',
    subtitle: 'Découvrez comment nous bâtissons le futur des services publics et privés.',
    cta: 'En savoir plus',
    articles: [
      { cat: 'Innovation', title: 'Transformation digitale', read: '5 min' },
      { cat: 'Stratégie', title: 'Expansion régionale', read: '8 min' },
      { cat: 'Tech', title: 'Nouvelle plateforme', read: '3 min' },
    ],
  },
  en: {
    tag: 'News',
    title: 'Our vision for a digital Africa',
    subtitle: 'Discover how we\'re building the future of public and private services.',
    cta: 'Learn more',
    articles: [
      { cat: 'Innovation', title: 'Digital transformation', read: '5 min' },
      { cat: 'Strategy', title: 'Regional expansion', read: '8 min' },
      { cat: 'Tech', title: 'New platform', read: '3 min' },
    ],
  },
  ar: {
    tag: 'أخبار',
    title: 'رؤيتنا لأفريقيا الرقمية',
    subtitle: 'اكتشف كيف نبني مستقبل الخدمات العامة والخاصة.',
    cta: 'اعرف المزيد',
    articles: [
      { cat: 'ابتكار', title: 'التحول الرقمي', read: '٥ د' },
      { cat: 'استراتيجية', title: 'التوسع الإقليمي', read: '٨ د' },
      { cat: 'تقنية', title: 'منصة جديدة', read: '٣ د' },
    ],
  },
};

const WebPlatform: React.FC = () => {
  const [lang, setLang] = useState<'fr' | 'en' | 'ar'>('fr');
  const [autoRotate, setAutoRotate] = useState(true);

  useEffect(() => {
    if (!autoRotate) return;
    const id = setInterval(() => {
      setLang((l) => (l === 'fr' ? 'en' : l === 'en' ? 'ar' : 'fr'));
    }, 3200);
    return () => clearInterval(id);
  }, [autoRotate]);

  const c = content[lang];
  const isRTL = lang === 'ar';

  return (
    <BrowserFrame url="institution.org">
      <div style={{
        padding: '1rem',
        background: 'var(--cream)',
        minHeight: '260px',
        fontFamily: "'Montserrat', sans-serif",
        direction: isRTL ? 'rtl' : 'ltr',
        textAlign: isRTL ? 'right' : 'left',
      }}>
        {/* Top bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.7rem' }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '.85rem', fontWeight: 700, color: 'var(--blue)' }}>
            Institution
          </div>
          <div style={{ display: 'flex', gap: '3px' }} onClick={() => setAutoRotate(false)}>
            {(['fr', 'en', 'ar'] as const).map((l) => (
              <button
                key={l}
                onClick={() => { setLang(l); setAutoRotate(false); }}
                style={{
                  padding: '.15rem .4rem',
                  background: lang === l ? 'var(--blue)' : 'transparent',
                  color: lang === l ? 'white' : 'var(--ink2)',
                  border: `1px solid ${lang === l ? 'var(--blue)' : 'var(--border)'}`,
                  fontSize: '.45rem', fontWeight: 700, borderRadius: '2px',
                  cursor: 'pointer', textTransform: 'uppercase',
                }}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Hero content */}
        <div style={{
          background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '3px',
          padding: '.8rem', marginBottom: '.6rem',
        }}>
          <div style={{
            fontSize: '.45rem', color: 'var(--gold-dark)',
            letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 700, marginBottom: '.3rem',
          }}>
            {c.tag}
          </div>
          <div style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: '.95rem', fontWeight: 600, color: 'var(--ink)',
            lineHeight: 1.2, marginBottom: '.3rem',
          }}>
            {c.title}
          </div>
          <div style={{ fontSize: '.55rem', color: 'var(--ink2)', lineHeight: 1.5, marginBottom: '.45rem' }}>
            {c.subtitle}
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '.3rem',
            padding: '.25rem .55rem', background: 'var(--blue)', color: 'white',
            fontSize: '.48rem', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
            borderRadius: '2px',
          }}>
            {c.cta} {isRTL ? '←' : '→'}
          </div>
        </div>

        {/* Article cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '.35rem' }}>
          {c.articles.map((a, i) => (
            <div key={i} style={{
              background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '3px',
              padding: '.4rem',
            }}>
              <div style={{
                height: '22px', background: `linear-gradient(135deg, ${['var(--blue)', 'var(--gold)', '#5E35B1'][i]}, var(--ink))`,
                borderRadius: '2px', marginBottom: '.3rem',
              }} />
              <div style={{ fontSize: '.4rem', color: 'var(--gold-dark)', letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700, marginBottom: '.15rem' }}>
                {a.cat}
              </div>
              <div style={{ fontSize: '.52rem', fontWeight: 600, color: 'var(--ink)', lineHeight: 1.3, marginBottom: '.2rem' }}>
                {a.title}
              </div>
              <div style={{ fontSize: '.4rem', color: 'var(--ink2)' }}>⏱ {a.read}</div>
            </div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  );
};

export default WebPlatform;
